import React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Fulfillment } from '@/types/fulfillment';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar, Briefcase, Users, Clipboard, Clock, Coins, Code } from 'lucide-react';

interface FulfillmentBoardDetailsProps {
  selectedProject: Fulfillment | null;
  onClose: () => void;
}

const FulfillmentBoardDetails = ({ selectedProject, onClose }: FulfillmentBoardDetailsProps) => {
  if (!selectedProject) return null;

  // Format date to be more readable
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not set';
    try {
      return format(new Date(dateString), 'MMMM d, yyyy');
    } catch (e) {
      return 'Invalid date';
    }
  };

  // Format budget to show as currency
  const formatBudget = (budget?: number) => {
    if (!budget && budget !== 0) return 'Not set';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(budget);
  };
  
  const renderTechStack = () => {
    if (!selectedProject.tech_stack || selectedProject.tech_stack.length === 0) {
      return <p className="text-muted-foreground">No tech stack specified</p>;
    }
    
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {selectedProject.tech_stack.map((tech: any, index: number) => (
          <Badge key={tech.name || tech} variant="outline" className="bg-accent/10">
            {tech.name || tech}
          </Badge>
        ))}
      </div>
    );
  };
  
  const renderDeliverables = () => {
    if (!selectedProject.deliverables || selectedProject.deliverables.length === 0) {
      return <p className="text-muted-foreground">No deliverables specified</p>;
    }
    
    return (
      <ul className="space-y-2 mt-2">
        {selectedProject.deliverables.map((deliverable: any, index: number) => (
          <li key={deliverable} className="flex items-start gap-2">
            <div className="h-5 w-5 rounded-full border border-accent/50 flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className={`h-2 w-2 rounded-full ${deliverable.completed ? 'bg-accent' : 'bg-gray-300'}`} />
            </div>
            <span>{deliverable.name || deliverable}</span>
          </li>
        ))}
      </ul>
    );
  };
  
  const renderTeamMembers = () => {
    if (!selectedProject.assigned_team_members || selectedProject.assigned_team_members.length === 0) {
      return <p className="text-muted-foreground">No team members assigned</p>;
    }
    
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {selectedProject.assigned_team_members.map((member: any, index: number) => (
          <Badge key={member.name || member} variant="outline" className="bg-purple-500/10">
            {member.name || member}
          </Badge>
        ))}
      </div>
    );
  };

  return (
    <Dialog open={!!selectedProject} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-semibold">{selectedProject.name}</h2>
            <div className="flex items-center mt-1 gap-2">
              <Badge variant="secondary" className="bg-accent/10 text-accent">
                {selectedProject.project_type}
              </Badge>
              {selectedProject.project_category && (
                <Badge variant="outline">{selectedProject.project_category}</Badge>
              )}
            </div>
          </div>
          
          <ScrollArea className="max-h-[60vh]">
            <div className="space-y-6">
              {selectedProject.description && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Description</h3>
                  <p className="text-sm">{selectedProject.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium">Start Date</h4>
                  </div>
                  <p className="text-sm pl-6">{formatDate(selectedProject.start_date)}</p>
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium">Target Completion</h4>
                  </div>
                  <p className="text-sm pl-6">{formatDate(selectedProject.target_completion_date)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Coins className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium">Budget</h4>
                  </div>
                  <p className="text-sm pl-6">{formatBudget(selectedProject.budget)}</p>
                </div>
                
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium">Estimated Hours</h4>
                  </div>
                  <p className="text-sm pl-6">{selectedProject.estimated_hours || 'Not estimated'}</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">Project Manager</h4>
                </div>
                <p className="text-sm pl-6">{selectedProject.project_manager || 'Not assigned'}</p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">Team Members</h4>
                </div>
                <div className="pl-6">
                  {renderTeamMembers()}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Code className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">Tech Stack</h4>
                </div>
                <div className="pl-6">
                  {renderTechStack()}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Clipboard className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">Deliverables</h4>
                </div>
                <div className="pl-6">
                  {renderDeliverables()}
                </div>
              </div>

              {selectedProject.requirements && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">Requirements</h4>
                  <p className="text-sm">{selectedProject.requirements}</p>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FulfillmentBoardDetails;
